// ─────────────────────────────────────────────────────────────────────────────
// dp-variant-datepicker.js
// Termin-Auswahl auf der Produktseite. Der Kalender selbst kommt aus
// dp-calendar.js (DpCalendar), hier steckt nur die Produkt-Logik:
//   • Varianten nach Tag gruppieren (Timestamp-Metafeld bzw. Titel)
//   • Button mit dem gewählten Termin + Popover mit dem Kalender
//   • Uhrzeit-Auswahl unterhalb, wenn ein Tag mehrere Termine hat
//   • Wechselt die Variante genau wie Impacts eigener Variant-Picker:
//     Section mit ?variant= neu laden → product:rerender + variant:change.
//     Funktioniert dadurch auch ohne "Variant picker"-Block im Template.
//   • Ist beim Aufruf ein vergangener/ausverkaufter Termin vorausgewählt
//     (z. B. über einen Collection-Link mit ?variant=), wird automatisch auf
//     den nächsten buchbaren Termin gewechselt.
// ─────────────────────────────────────────────────────────────────────────────

const LOW_STOCK_THRESHOLD = 6; // < 6 Tickets → oranger Balken

// Timestamp nur verwenden, wenn er plausibel ist (Sekunden, nicht ms,
// und im Bereich ±10 Jahre um heute). Sonst Fallback auf den Titel.
const NOW_S = Date.now() / 1000;
const TEN_YEARS = 10 * 365 * 86400;

function validTs(v) {
  const ts = Number(v.ts);
  if (!ts || !isFinite(ts)) return null;
  if (Math.abs(ts - NOW_S) > TEN_YEARS) {
    console.warn(`[dp] Unplausibler Timestamp bei Variante ${v.id} (${v.title}):`, v.ts);
    return null;
  }
  return Math.round(ts);
}

// Produkt-JSON (/products/handle.js) einmal laden – liefert die vollständigen
// Varianten-Objekte für das variant:change-Event (Galerie, Sticky-Bar …)
let _productJson = null;
function loadProductJson() {
  if (!_productJson) {
    const path = window.location.pathname.replace(/\/$/, '');
    _productJson = fetch(`${path}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null);
  }
  return _productJson;
}

function whenCalendar(cb) {
  if (window.DpCalendar) cb(window.DpCalendar);
  else document.addEventListener('dp:calendar-ready', () => cb(window.DpCalendar), { once: true });
}


whenCalendar((DpCalendar) => {
  const { pad, esc, toKey, tsToKey, tsToTime, todayKey, formatShort } = DpCalendar.util;

  // Datum: Metafeld-Timestamp, Fallback auf "14.10.2026" im Variantentitel
  function dateKeyOf(v) {
    const ts = validTs(v);
    if (ts) return tsToKey(ts);
    const m = (v.title || '').match(/(\d{1,2})\.(\d{1,2})\.(\d{4})/);
    return m ? toKey(+m[3], +m[2] - 1, +m[1]) : null;
  }

  // Uhrzeit "18:00": Metafeld-Timestamp, Fallback auf den Titel
  function timeOf(v) {
    const ts = validTs(v);
    if (ts) {
      const t = tsToTime(ts);
      if (t !== '00:00') return t;
    }
    const m = (v.title || '').match(/(\d{1,2}):(\d{2})/);
    return m ? `${pad(m[1])}:${m[2]}` : '';
  }

  // "Mi., 04.11.2026, 18:00 Uhr"
  function formatFull(v) {
    if (!v.key) return v.title;
    return formatShort(v.key) + (v.time ? `, ${v.time} Uhr` : '');
  }


  class DpVariantDatepicker extends HTMLElement {
    connectedCallback() {
      if (!this._setupDone && !this._setup()) return;
      this._bind();
      this.setAttribute('ready', '');

      // Vergangener/ausverkaufter Termin vorausgewählt → nächsten freien nehmen
      const sel = this._selected();
      if (!sel || !this._isBookable(sel)) {
        const next = this.variants
          .filter((v) => v.key && this._isBookable(v))
          .sort((a, b) => a.key.localeCompare(b.key) || a.time.localeCompare(b.time))[0];
        if (next && (!sel || next.id !== sel.id)) this._select(next);
      }
    }

    disconnectedCallback() {
      this._unbind();
    }

    // ─── Einmaliges Setup ──────────────────────────────────────────────────
    _setup() {
      const dataEl = this.querySelector('.dpvd__data');
      if (!dataEl) return false;

      try {
        this.variants = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.warn('[dp] Datepicker-Daten fehlerhaft', e);
        return false;
      }

      this.uid = Math.random().toString(36).slice(2, 8); // eindeutige IDs, auch bei mehreren Pickern
      this.today = todayKey();
      this.byDay = new Map();

      this.variants.forEach((v) => {
        v.key = dateKeyOf(v);
        v.time = timeOf(v);
        if (!v.key) return;
        if (!this.byDay.has(v.key)) this.byDay.set(v.key, []);
        this.byDay.get(v.key).push(v);
      });
      this.byDay.forEach((list) => {
        list.sort((a, b) => a.time.localeCompare(b.time) || (validTs(a) || 0) - (validTs(b) || 0));
      });

      const keys = [...this.byDay.keys()].sort();
      const upcoming = keys.filter((k) => k >= this.today);

      this.selectedId = Number(this.dataset.selectedId) || null;
      this.isOpen = false;

      this.trigger = this.querySelector('.dpvd__trigger');
      this.valueEl = this.querySelector('.dpvd__value');
      this.popoverEl = this.querySelector('.dpvd__popover');
      this.timesEl = this.querySelector('.dpvd__times');

      const sel = this._selected();
      this.cal = new DpCalendar(this.querySelector('.dpvd__calendar'), {
        showBars: true,
        minMonth: (upcoming[0] || keys[0] || this.today).slice(0, 7),
        maxMonth: (keys[keys.length - 1] || this.today).slice(0, 7),
        view: (sel && sel.key) || upcoming[0] || keys[0] || this.today,
        getDay: (key) => this._getDay(key),
        onSelect: (key) => this._onDay(key)
      });

      this.trigger.addEventListener('click', () => (this.isOpen ? this.close() : this.open()));

      // Uhrzeit-Auswahl unterhalb (Radios wie im Impact-Picker)
      this.timesEl.addEventListener('change', (e) => {
        e.stopPropagation(); // nicht als Formular-Änderung ans Theme durchreichen
        const input = e.target.closest('input[type="radio"]');
        if (!input || !input.checked) return;
        const v = this._byId(Number(input.value));
        if (v) this._select(v, { keepOpen: true });
      });

      // Globale Listener (werden in _bind/_unbind verwaltet)
      this._onDocClick = (e) => {
        // composedPath statt contains(): geklickte Buttons werden beim
        // Re-Render ersetzt und wären sonst schon "außerhalb"
        const path = e.composedPath();
        if (this.isOpen && !path.includes(this.popoverEl) && !path.includes(this.trigger)) this.close();
      };
      this._onKeydown = (e) => {
        if (e.key === 'Escape' && this.isOpen) {
          this.close();
          this.trigger.focus();
        }
      };
      this._onVariantChange = (e) => {
        const id = e.detail?.variant?.id;
        if (!id || id === this.selectedId || !this._byId(id)) return;
        this.selectedId = id;
        this._updateTrigger();
        this._renderTimes();
        if (this.isOpen) this.cal.render();
      };

      this._updateTrigger();
      this._renderTimes();
      this._setupDone = true;
      return true;
    }

    _bind() {
      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onKeydown);
      document.addEventListener('variant:change', this._onVariantChange);
    }

    _unbind() {
      if (!this._setupDone) return;
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onKeydown);
      document.removeEventListener('variant:change', this._onVariantChange);
    }

    // ─── Helfer ────────────────────────────────────────────────────────────
    _byId(id) {
      return this.variants.find((v) => v.id === id) || null;
    }

    _selected() {
      return this._byId(this.selectedId);
    }

    _isBookable(v) {
      return v.available && v.key >= this.today;
    }

    // Tagesdaten für den Kalender
    _getDay(key) {
      const list = this.byDay.get(key);
      if (!list || key < this.today) return null; // vergangen = wie kein Termin

      const sel = this._selected();
      const bookable = list.filter((v) => this._isBookable(v));
      const isSelected = !!sel && sel.key === key;

      // Uhrzeit unter dem Tag: gewählte bzw. erste buchbare, bei mehreren "+n"
      const shown = (isSelected && sel) || bookable[0] || list[0];
      const sub = shown.time ? shown.time + (list.length > 1 ? ` +${list.length - 1}` : '') : '';
      const times = list.map((v) => v.time).filter(Boolean);

      return {
        selectable: bookable.length > 0,
        selected: isSelected,
        soldout: bookable.length === 0,
        low: bookable.length > 0 &&
          bookable.every((v) => v.qty !== null && v.qty < LOW_STOCK_THRESHOLD),
        sub,
        label: [
          times.length ? `${times.join(', ')} Uhr` : '',
          bookable.length && bookable.every((v) => v.qty !== null && v.qty < LOW_STOCK_THRESHOLD)
            ? 'nur noch wenige Tickets' : ''
        ].filter(Boolean).join(', ')
      };
    }

    // ─── Öffnen / Schließen ────────────────────────────────────────────────
    open() {
      const sel = this._selected();
      if (sel && sel.key) this.cal.setView(sel.key);
      this.isOpen = true;
      this.cal.render();
      this.popoverEl.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.isOpen = false;
      this.popoverEl.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    // ─── Uhrzeit-Auswahl ───────────────────────────────────────────────────
    // Nur bei mehreren Terminen am gewählten Tag. Markup wie Impacts
    // Variant-Picker (block-swatch), damit es identisch aussieht.
    _renderTimes() {
      const sel = this._selected();
      const list = sel && sel.key ? this.byDay.get(sel.key) || [] : [];

      if (list.length < 2) {
        this.timesEl.hidden = true;
        this.timesEl.innerHTML = '';
        return;
      }

      const name = `dpvd-time-${this.uid}`;
      const swatches = list.map((v, i) => {
        const id = `${name}-${i}`;
        const ok = this._isBookable(v);
        return `<input class="sr-only" type="radio" name="${name}" id="${id}" value="${v.id}"`
          + (v.id === sel.id ? ' checked' : '')
          + (ok ? '' : ' disabled')
          + '>'
          + `<label class="block-swatch${ok ? '' : ' is-disabled'}" for="${id}">`
          + `<span>${esc(v.time ? `${v.time} Uhr` : formatFull(v))}</span></label>`;
      }).join('');

      this.timesEl.innerHTML =
        '<legend class="dpvd__label">Uhrzeit auswählen</legend>'
        + `<div class="variant-picker__option-values wrap gap-2">${swatches}</div>`;
      this.timesEl.hidden = false;
    }

    _updateTrigger() {
      const sel = this._selected();
      if (sel) this.valueEl.textContent = formatFull(sel);
    }

    // ─── Auswahl ───────────────────────────────────────────────────────────
    _onDay(key) {
      const list = this.byDay.get(key) || [];
      const bookable = list.filter((v) => this._isBookable(v));
      if (!bookable.length) return;

      // Gleicher Tag wie bisher → Auswahl behalten, sonst erster freier Termin.
      // Bei mehreren Terminen wählt man die Uhrzeit danach unterhalb.
      const sel = this._selected();
      const keep = sel && sel.key === key && this._isBookable(sel);
      this._select(keep ? sel : bookable[0]);
    }

    _select(variant, { keepOpen = false } = {}) {
      this.selectedId = variant.id;
      this._updateTrigger();
      this._renderTimes();
      if (!keepOpen) this.close();
      this._applyToForm(variant);
    }

    // Variante wechseln – nachgebaut nach Impacts VariantPicker.selectCombination():
    //   1. Section mit ?variant= laden
    //   2. id-Feld im Formular setzen + URL aktualisieren
    //   3. product:rerender → Impact tauscht Preis, Buy-Button, Liquid-Blöcke …
    //   4. variant:change   → Galerie, Sticky-Bar & Co. reagieren
    // Klappt etwas nicht, wird die Seite mit ?variant= neu geladen.
    async _applyToForm(variant) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);

      const rerender = this.closest('product-rerender');
      const form = document.forms[rerender?.getAttribute('observe-form')]
        || document.querySelector('form[action*="/cart/add"]');
      const sectionId = this.closest('.shopify-section')?.id.replace(/^shopify-section-/, '');

      if (!form || !form.elements.id || !sectionId) {
        window.location.assign(url.toString());
        return;
      }

      const previousId = Number(form.elements.id.value);
      if (previousId === variant.id) return;

      try {
        const [html, product] = await Promise.all([
          fetch(`${window.location.pathname}?variant=${variant.id}&section_id=${sectionId}`)
            .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.text(); }),
          loadProductJson()
        ]);

        const fullVariant = product?.variants?.find((v) => v.id === variant.id) || { id: variant.id, available: variant.available };
        const previousVariant = product?.variants?.find((v) => v.id === previousId) || null;
        const fragment = document.createRange().createContextualFragment(html);

        form.elements.id.value = variant.id;
        form.elements.id.dispatchEvent(new Event('change', { bubbles: true }));
        window.history.replaceState({ path: url.toString() }, '', url.toString());

        form.dispatchEvent(new CustomEvent('product:rerender', {
          detail: { htmlFragment: fragment, productChange: false }
        }));
        form.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: { formId: form.elements.id, variant: fullVariant, previousVariant }
        }));
        window.Shopify?.PaymentButton?.init?.();
      } catch (e) {
        console.warn('[dp] Variantenwechsel per AJAX fehlgeschlagen, lade neu', e);
        window.location.assign(url.toString());
      }
    }
  }

  if (!customElements.get('dp-variant-datepicker')) {
    customElements.define('dp-variant-datepicker', DpVariantDatepicker);
  }
});